import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuChevronRight, LuFolderKanban, LuHash, LuLayers, LuRoute, LuUser } from 'react-icons/lu';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { normalizePatientId } from '../../../../utils/patientAccess';
import { StepCircle, useStepStatusText } from '../VisitStepper';
import { PANEL } from '../VisitUi';

const STATUS_TONES = {
  missing: 'tw-text-amber-700 dark:tw-text-amber-300',
  done: 'tw-text-emerald-700 dark:tw-text-emerald-300',
  info: 'tw-text-slate-500 dark:tw-text-slate-400',
  optional: 'tw-text-slate-500 dark:tw-text-slate-400',
};

/** "Bilgiler": the project and patient at a glance, and a map of the steps. */
const InformationStep = ({ projectTitle, projectPhase, projectCentersType, patientId, visitNumber, steps, onSelectStep }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`visitModal.${key}`, vars);
  const statusText = useStepStatusText();
  const notSpecified = label('common.notSpecified');

  const facts = [
    { key: 'project', icon: LuFolderKanban, value: projectTitle || label('info.projectMissing'), muted: !projectTitle },
    { key: 'phase', icon: LuLayers, value: projectPhase || notSpecified, muted: !projectPhase },
    { key: 'centersType', icon: LuBuilding2, value: projectCentersType || notSpecified, muted: !projectCentersType },
    { key: 'patientId', icon: LuUser, value: normalizePatientId(patientId) || notSpecified, muted: !normalizePatientId(patientId) },
    {
      key: 'visitNumber',
      icon: LuHash,
      value: visitNumber ? label('info.visitNumberValue', { n: visitNumber }) : notSpecified,
      muted: !visitNumber,
    },
  ];

  return (
    <div className="tw-space-y-5">
      <dl className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
        {facts.map(({ key, icon: Icon, value, muted }) => (
          <div key={key} className={`${PANEL} tw-flex tw-items-start tw-gap-3 tw-p-4`}>
            <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-sky-500/10 tw-text-sky-700 tw-ring-1 tw-ring-inset tw-ring-sky-500/20 dark:tw-text-sky-300">
              <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </span>
            <div className="tw-min-w-0">
              <dt className="tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label(`info.${key}`)}</dt>
              <dd className={`tw-mt-0.5 tw-break-words tw-text-[15px] tw-font-semibold ${muted ? 'tw-text-slate-400 dark:tw-text-slate-500' : 'tw-text-slate-900 dark:tw-text-white'}`}>
                {value}
              </dd>
            </div>
          </div>
        ))}
      </dl>

      <section className={`${PANEL} tw-p-4 sm:tw-p-5`}>
        <div className="tw-flex tw-items-start tw-gap-3">
          <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-teal-500/10 tw-text-teal-700 tw-ring-1 tw-ring-inset tw-ring-teal-500/25 dark:tw-text-teal-300">
            <LuRoute aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </span>
          <div className="tw-min-w-0">
            <h3 className="tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('info.roadmapTitle')}</h3>
            <p className="tw-mt-0.5 tw-text-[13px] tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{label('info.roadmapHint')}</p>
          </div>
        </div>

        <ol className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-2 md:tw-grid-cols-2">
          {steps.map((step, index) => step.key !== 'informations' && (
            <li key={step.key} className="tw-min-w-0">
              <button
                type="button"
                onClick={() => onSelectStep(step.key)}
                className={`tw-group tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                  step.state === 'missing'
                    ? 'tw-border-amber-500/30 tw-bg-amber-400/[0.06] hover:tw-bg-amber-400/[0.12]'
                    : 'tw-border-slate-200/80 tw-bg-white/60 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.02] dark:hover:tw-bg-white/[0.05]'
                }`}
              >
                <StepCircle state={step.state} number={index + 1} size="sm" />
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                    {label(`steps.${step.key}.label`)}
                  </span>
                  <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label(`steps.${step.key}.short`)}</span>
                </span>
                <span className={`tw-shrink-0 tw-text-xs tw-font-semibold ${STATUS_TONES[step.state]}`}>{statusText(step)}</span>
                <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400 tw-transition-transform group-hover:tw-translate-x-0.5" />
              </button>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
};

InformationStep.propTypes = {
  projectTitle: PropTypes.string,
  projectPhase: PropTypes.string,
  projectCentersType: PropTypes.string,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visitNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  steps: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, state: PropTypes.string.isRequired })).isRequired,
  onSelectStep: PropTypes.func.isRequired,
};

export default InformationStep;
